import { buildOrganisationLink, buildSchoolLink } from 'util/linkBuilder';
import { pxToEm } from 'util/pxToEm';
import { Box, Typography } from '@mui/material';
import { Image } from 'components/lib';
import {
  EventAdminStatus,
  ConfirmationModal,
  EventBriefProps,
  Divider,
  ViewAllDatesModal,
} from 'components/ui';
import { useRouter } from 'next/router';
import React, { ReactElement, useMemo, useState } from 'react';
import { Event, useEventsApi } from 'services/api';
import { Routes, adminEventReviewUrl } from 'services/routes';
import { EventBriefAdmin } from 'components/ui/EventBrief/EventBriefAdmin';
import { EVENT_STATUS_FILTERS_ENUM, EventStatus } from 'enum/event-status.enum';
import useSWR from 'swr';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ApproveIcon from '../../../assets/icons/approve-label.svg';

const DAYS_OF_WEEK_NAMES: Record<number, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

export interface AdminEventPanelProps {
  event: Event;
  onChange: () => void;
}

export const AdminEventPanel = ({
  event,
  onChange,
}: AdminEventPanelProps): ReactElement => {
  const [rejectEventModalId, setRejectEventModalId] = useState<string | null>(
    null,
  );
  const [cancelEventModalId, setCancelEventModalId] = useState<string | null>(
    null,
  );
  const [liveEventModalId, setLiveEventModalId] = useState<string | null>(null);
  const [reviewEventModalId, setReviewEventModalId] = useState<string | null>(
    null,
  );
  const [showViewAllDatesModal, setShowViewAllDatesModal] = useState(false);
  const [occurrenceFilter, setOccurrenceFilter] = useState<'upcoming' | 'past'>('upcoming');

  const router = useRouter();
  const eventsApi = useEventsApi();

  const { data: occurrences, isValidating: occurrencesLoading, mutate: mutateOccurrences } = useSWR(
    event.isRecurring && showViewAllDatesModal
      ? `/events/${event.id}/occurrences?filter=${occurrenceFilter}`
      : null,
    async () => {
      const result = await eventsApi.getEventOccurrences(event.id, occurrenceFilter);
      if (result.success) return result.value;
      return [];
    },
  );

  const { data: occurrenceCounts, mutate: mutateOccurrenceCounts } = useSWR(
    event.isRecurring ? `/events/${event.id}/occurrence-counts` : null,
    async () => {
      const result = await eventsApi.getEventOccurrenceCounts(event.id);
      if (result.success) return result.value;
      return { upcoming: 0, past: 0 };
    },
  );

  const handleViewAttendees = (occurrenceId: string) => {
    router.push(Routes.admin.eventAttendees(occurrenceId));
  };

  const handleCheckInAttendees = (occurrenceId: string) => {
    router.push(Routes.admin.eventCheckIn(occurrenceId));
  };

  const handleDeleteOccurrence = async (occurrenceId: string) => {
    const result = await eventsApi.deleteChildOccurrence(occurrenceId);
    if (result.success) {
      mutateOccurrences();
      mutateOccurrenceCounts();
      onChange();
    }
  };

  const recurrenceLabel = useMemo(() => {
    if (!event.isRecurring || !event.recurrenceRule) return null;

    const { interval, daysOfWeek, seriesEndDate } = event.recurrenceRule;
    const dayNames = daysOfWeek
      .map(d => DAYS_OF_WEEK_NAMES[d])
      .filter(Boolean);

    const daysText = dayNames.length === 1
      ? dayNames[0]
      : dayNames.join(' and ');

    const endDate = new Date(seriesEndDate);
    const formattedDate = endDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    const intervalText = interval === 1 ? 'week' : `${String(interval)} weeks`;

    return `Repeats every ${intervalText} on ${daysText}\nUntil ${formattedDate}`;
  }, [event.isRecurring, event.recurrenceRule]);

  const map = (e: Event): EventBriefProps & { isRecurring?: boolean; recurrenceLabel?: string; status: Event['status'] } => {
    return {
      id: e.id,
      status: e.status,
      title: e.name,
      link: e.url,
      date: new Date(e.date),
      favorite: false, // TODO
      startTime: e.startTime,
      endTime: e.endTime,
      bannerSrc: e.flyerImageUrl,
      isRecurring: e.isRecurring,
      recurrenceLabel: recurrenceLabel || undefined,
      school: {
        iconUrl: e.school.iconUrl,
        logoUrl: e.school.logoUrl,
        link: buildSchoolLink(e.school.slug, router),
        name: e.school.name,
      },
      organizations: e.hostOrganizations.map(o => ({
        logoUrl: o.logoUrl,
        link: buildOrganisationLink(o.id, e.school.slug, router),
        name: o.name,
      })),
    };
  };

  const mappedEvent = map(event);

  const approveEvent = async (eventId: Event['id']) => {
    setLiveEventModalId(null);
    await eventsApi.approveEvent(eventId);
    onChange?.();
  };

  const rejectEvent = async (eventId: Event['id']) => {
    setRejectEventModalId(null);
    await eventsApi.rejectEvent(eventId);
    onChange?.();
  };

  const cancelEvent = async (eventId: Event['id']) => {
    setCancelEventModalId(null);
    await eventsApi.cancelEvent(eventId);
    onChange?.();
  };

  const reviewEvent = async (eventId: Event['id']) => {
    setReviewEventModalId(null);
    await eventsApi.moveToNeedsReviewEvent(eventId);
    onChange?.();
  };

  const getEventStatus = (event: Event): EventStatus => {
    if (
      ['PUBLISHED', 'UPDATED', 'RESCHEDULED', 'FINISHED'].includes(event.status)
    )
      return EVENT_STATUS_FILTERS_ENUM.LIVE_ON_APP;
    if (['CANCELLED'].includes(event.status))
      return EVENT_STATUS_FILTERS_ENUM.CANCELED;
    if (['PENDING_APPROVAL'].includes(event.status))
      return EVENT_STATUS_FILTERS_ENUM.NEEDS_REVIEW;
    if (['REJECTED'].includes(event.status))
      return EVENT_STATUS_FILTERS_ENUM.REQUIRES_EDITS;
    if (['EXPIRED'].includes(event.status))
      return EVENT_STATUS_FILTERS_ENUM.EXPIRED;

    return EVENT_STATUS_FILTERS_ENUM.LIVE_ON_APP;
  };

  const handleEventStatusChange = (status: EventStatus) => {
    if (status === EVENT_STATUS_FILTERS_ENUM.LIVE_ON_APP)
      setLiveEventModalId(event.id);

    if (status === EVENT_STATUS_FILTERS_ENUM.REQUIRES_EDITS)
      setRejectEventModalId(event.id);

    if (status === EVENT_STATUS_FILTERS_ENUM.CANCELED)
      setCancelEventModalId(event.id);

    if (status === EVENT_STATUS_FILTERS_ENUM.NEEDS_REVIEW)
      setReviewEventModalId(event.id);
  };

  return (
    <>
      <Box
        mt={24}
        mb={16}
        sx={{
          maxWidth: 320,
          minWidth: 320,
          border: '1px solid #DEDEDE',
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <EventAdminStatus
            event={event}
            status={getEventStatus(event)}
            onChange={handleEventStatusChange}
          />
          <EventBriefAdmin {...mappedEvent} hideFavorite />
        </Box>
        <Box
          sx={{
            paddingTop: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <Divider style={{ borderColor: '#DEDEDE', height: '1px' }} />

          {event.isRecurring && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  py: '4px',
                }}
                onClick={() => router.push(adminEventReviewUrl(event.id, event.status))}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ApproveIcon style={{ width: 18, height: 18 }} />
                  <Typography
                    sx={{
                      fontSize: pxToEm(14),
                      fontWeight: 400,
                      color: '#000000',
                    }}
                  >
                    Approvals
                  </Typography>
                </Box>
                <ChevronRightIcon sx={{ fontSize: 20, color: '#999999' }} />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  py: '4px',
                }}
                onClick={() => router.push(Routes.editEvent(event.id))}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Image src="/icons/edit_square.svg" alt="Edit Main Event" />
                  <Typography
                    sx={{
                      fontSize: pxToEm(14),
                      fontWeight: 400,
                      color: '#000000',
                    }}
                  >
                    Edit Main Event
                  </Typography>
                </Box>
                <ChevronRightIcon sx={{ fontSize: 20, color: '#999999' }} />
              </Box>
            </>
          )}

          {!event.isRecurring && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  py: '4px',
                }}
                onClick={() => router.push(Routes.editEvent(event.id))}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Image src="/icons/edit_square.svg" alt="Edit Event" />
                  <Typography
                    sx={{
                      fontSize: pxToEm(14),
                      fontWeight: 400,
                      color: '#000000',
                    }}
                  >
                    Edit Event
                  </Typography>
                </Box>
                <ChevronRightIcon sx={{ fontSize: 20, color: '#999999' }} />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  py: '4px',
                }}
                onClick={() => router.push(Routes.admin.eventAttendees(event.id))}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Image src="/icons/attendees.svg" alt="Attendees List" />
                  <Typography
                    sx={{
                      fontSize: pxToEm(14),
                      fontWeight: 400,
                      color: '#000000',
                    }}
                  >
                    Attendees List
                  </Typography>
                </Box>
                <ChevronRightIcon sx={{ fontSize: 20, color: '#999999' }} />
              </Box>
            </>
          )}
        </Box>
      </Box>

      {rejectEventModalId && (
        <ConfirmationModal
          open
          message="Confirm that you would like to reject this event."
          title="Reject Event"
          confirmAction="Reject Event"
          onCancel={() => setRejectEventModalId(null)}
          onConfirm={() => rejectEvent(rejectEventModalId)}
          body={
            <Typography
              color="primary"
              style={{ fontWeight: 600, marginBottom: 20 }}
            >
              {event.name}
            </Typography>
          }
        />
      )}

      {cancelEventModalId && (
        <ConfirmationModal
          open
          message="Confirm that you would like to cancel this event."
          title="Cancel Event"
          cancelAction="Back"
          confirmAction="Cancel Event"
          onCancel={() => setCancelEventModalId(null)}
          onConfirm={() => cancelEvent(cancelEventModalId)}
          body={
            <Typography
              color="primary"
              style={{ fontWeight: 600, marginBottom: 20 }}
            >
              {event.name}
            </Typography>
          }
        />
      )}

      {liveEventModalId && (
        <ConfirmationModal
          open
          message="Confirm that you would like to move this event to live on app."
          title="Move to Live on App"
          confirmAction="Move to Live on App"
          onCancel={() => setLiveEventModalId(null)}
          onConfirm={() => approveEvent(liveEventModalId)}
          body={
            <Typography
              color="primary"
              style={{ fontWeight: 600, marginBottom: 20 }}
            >
              {event.name}
            </Typography>
          }
        />
      )}

      {reviewEventModalId && (
        <ConfirmationModal
          open
          title="Move to Needs Review"
          message="Confirm that you would like to move this event to needs review."
          confirmAction="Move to Needs Review"
          onCancel={() => setReviewEventModalId(null)}
          onConfirm={() => reviewEvent(reviewEventModalId)}
          body={
            <Typography
              color="primary"
              style={{ fontWeight: 600, marginBottom: 20 }}
            >
              {event.name}
            </Typography>
          }
        />
      )}

      {event.isRecurring && (
        <ViewAllDatesModal
          open={showViewAllDatesModal}
          onClose={() => setShowViewAllDatesModal(false)}
          eventTitle={event.name}
          occurrences={occurrences || []}
          isLoading={occurrencesLoading}
          filter={occurrenceFilter}
          onFilterChange={setOccurrenceFilter}
          counts={occurrenceCounts}
          onViewAttendees={handleViewAttendees}
          onCheckInAttendees={handleCheckInAttendees}
          onDeleteOccurrence={handleDeleteOccurrence}
          mode="admin"
        />
      )}
    </>
  );
};
